import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MdDeleteOutline, MdLocationOn } from "react-icons/md";
import { LiaEditSolid } from "react-icons/lia";
import { RootState } from "../../../Redux/store";
import {
  removeAddress,
  selectAddresses,
  selectDefaultAddressId,
  setDefaultAddress,
  upsertAddress,
} from "../../../Redux/reducer/addressReducer";
import { SavedAddress } from "../../../types/ordering";
import usePageMeta from "../../../common/hooks/usePageMeta";
import { chawlaispat } from "../../../Redux/apiRoutes";

interface FormState {
  id: string;
  label: string;
  contactPerson: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  pinCode: string;
}

const emptyForm: FormState = {
  id: "",
  label: "",
  contactPerson: "",
  phone: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  pinCode: "",
};

const inputClass =
  "w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-turkishBlue-default";

const AddressBook = () => {
  const dispatch = useDispatch();
  const addresses = useSelector(selectAddresses);
  const defaultId = useSelector(selectDefaultAddressId);
  const userDetails = useSelector(
    (state: RootState) => state.Login.ContractorData
  );

  usePageMeta(
    "Address Book | Chawla Ispat",
    "Delivery ke address save karein — order karte waqt yahi chunna hoga.",
    chawlaispat
  );

  const [form, setForm] = useState<FormState | null>(null);

  // Naya address hamesha profile ke city aur pinCode se bhara hua khulega
  const prefill = useMemo<FormState>(
    () => ({
      ...emptyForm,
      contactPerson: userDetails?.name || "",
      phone: userDetails?.phoneNumber || "",
      city: userDetails?.city || "",
      pinCode: userDetails?.pinCode ? String(userDetails.pinCode) : "",
    }),
    [userDetails]
  );

  const openNew = () => setForm({ ...prefill });

  const openEdit = (a: SavedAddress) =>
    setForm({
      id: a.id,
      label: a.label || "",
      contactPerson: a.contactPerson || "",
      phone: a.phone || "",
      address1: a.address1,
      address2: a.address2 || "",
      city: a.city,
      state: a.state || "",
      pinCode: a.pinCode,
    });

  const set = (key: keyof FormState, value: string) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const handleSave = () => {
    if (!form) return;
    if (!form.address1.trim()) {
      toast.warning("Address line 1 bharna zaroori hai.");
      return;
    }
    if (!form.city.trim()) {
      toast.warning("City bharni zaroori hai.");
      return;
    }
    if (!/^[0-9]{6}$/.test(form.pinCode.trim())) {
      toast.warning("PIN code 6 digit ka hona chahiye.");
      return;
    }
    if (form.phone.trim() && !/^[0-9]{10}$/.test(form.phone.trim())) {
      toast.warning("Phone 10 digit ka hona chahiye.");
      return;
    }
    const wasEdit = !!form.id;
    dispatch(
      upsertAddress({
        id: form.id || undefined,
        label: form.label,
        contactPerson: form.contactPerson,
        phone: form.phone,
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        state: form.state,
        pinCode: form.pinCode,
      })
    );
    toast.success(wasEdit ? "Address update ho gaya." : "Address save ho gaya.");
    setForm(null);
  };

  const handleDelete = (a: SavedAddress) => {
    dispatch(removeAddress(a.id));
    if (form?.id === a.id) setForm(null);
    toast.info("Address hata diya.");
  };

  return (
    <div className="w-full px-3 sm:px-6 py-4 font-poppins">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-lg sm:text-xl font-bold">Address Book</h1>
          <p className="text-xs text-gray-500 mt-1">
            Delivery godown aapke PIN code se apne aap chuna jayega.
          </p>
        </div>
        <button
          type="button"
          onClick={openNew}
          className="shrink-0 rounded-md bg-turkishBlue-default text-white text-xs sm:text-sm px-3 py-2">
          + Naya address
        </button>
      </div>

      {!addresses.length && !form && (
        <div className="border border-dashed rounded-md py-10 text-center text-sm text-gray-500">
          Abhi koi address save nahi hai.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {addresses.map((a) => (
          <div
            key={a.id}
            className={`relative rounded-md border p-3 bg-white ${
              a.id === defaultId
                ? "border-turkishBlue-default"
                : "border-gray-200"
            }`}>
            <div className="flex items-start gap-2">
              <MdLocationOn className="text-turkishBlue-default mt-[2px] shrink-0" />
              <div className="text-sm text-gray-800 min-w-0">
                <p className="font-semibold">
                  {a.label || a.contactPerson || "Address"}
                  {a.id === defaultId && (
                    <span className="ml-2 align-middle text-[10px] bg-turkishBlue-default text-white rounded-full px-2 py-[2px]">
                      Default
                    </span>
                  )}
                </p>
                {a.contactPerson && a.label && (
                  <p className="text-xs text-gray-600">{a.contactPerson}</p>
                )}
                <p className="text-xs text-gray-600 mt-1 break-words">
                  {a.address1}
                  {a.address2 ? `, ${a.address2}` : ""}
                </p>
                <p className="text-xs text-gray-600">
                  {a.city}
                  {a.state ? `, ${a.state}` : ""} - {a.pinCode}
                </p>
                {a.phone && (
                  <p className="text-xs text-gray-500 mt-1">Ph: {a.phone}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 mt-3 pt-2 border-t">
              {a.id !== defaultId && (
                <button
                  type="button"
                  className="text-xs text-turkishBlue-default underline"
                  onClick={() => dispatch(setDefaultAddress(a.id))}>
                  Default banayein
                </button>
              )}
              <button
                type="button"
                className="text-xs text-gray-600 flex items-center gap-1"
                onClick={() => openEdit(a)}>
                <LiaEditSolid /> Edit
              </button>
              <button
                type="button"
                className="text-xs text-red-600 flex items-center gap-1 ml-auto"
                onClick={() => handleDelete(a)}>
                <MdDeleteOutline /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="mt-5 rounded-md border border-gray-200 bg-white p-3 sm:p-4">
          <h2 className="font-semibold text-sm mb-3">
            {form.id ? "Address edit karein" : "Naya address"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Label</label>
              <input
                className={inputClass}
                value={form.label}
                placeholder="Godown / Site 2"
                onChange={(e) => set("label", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Contact person</label>
              <input
                className={inputClass}
                value={form.contactPerson}
                onChange={(e) => set("contactPerson", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Phone</label>
              <input
                className={inputClass}
                value={form.phone}
                inputMode="numeric"
                maxLength={10}
                onChange={(e) => set("phone", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">
                Address line 1 <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                value={form.address1}
                onChange={(e) => set("address1", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Address line 2</label>
              <input
                className={inputClass}
                value={form.address2}
                onChange={(e) => set("address2", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">
                City <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">State</label>
              <input
                className={inputClass}
                value={form.state}
                onChange={(e) => set("state", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">
                PIN code <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                value={form.pinCode}
                inputMode="numeric"
                maxLength={6}
                onChange={(e) => set("pinCode", e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button
              type="button"
              onClick={handleSave}
              className="rounded-md bg-turkishBlue-default text-white text-sm px-4 py-2">
              Save
            </button>
            <button
              type="button"
              onClick={() => setForm(null)}
              className="text-sm text-gray-600 underline">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressBook;
